export type ProgrammingStatusKey =
  | 'NotStarted'
  | 'DocUploaded'
  | 'Reviewing'
  | 'ClientReview'
  | 'Approved'
  | 'Building'
  | 'ReadyForQA'
  | 'QA'
  | 'ReturnedForFixes'
  | 'QARetest'
  | 'QAApproved'
  | 'ClientApproval'
  | 'Complete'
  | 'Translations'
  | 'SoftLaunch'
  | 'FullLaunch'
  | 'DataProcessing'
  | 'Completed';

export interface ProgrammingStatusOption {
  key: ProgrammingStatusKey;
  label: string;
}

// Workflow order; the backend only ever moves a project forward through this list on events.
// The stored keys stay as they were: QA shows as "In QA", ClientApproval as "Client QA" and
// Complete as "Ready to Launch". Ready for QA to QA approved match Zoho's 2a-2e programming
// statuses; the Field statuses (Translations on) match Zoho 4-7 and 9 and only show on the
// Programming board.
export const PROGRAMMING_STATUSES: ProgrammingStatusOption[] = [
  { key: 'NotStarted', label: 'Not started' },
  { key: 'DocUploaded', label: 'Doc uploaded' },
  { key: 'Reviewing', label: 'Reviewing' },
  { key: 'ClientReview', label: 'Client Review' },
  { key: 'Approved', label: 'Approved' },
  { key: 'Building', label: 'Building' },
  { key: 'ReadyForQA', label: 'Ready for QA' },
  { key: 'QA', label: 'In QA' },
  { key: 'ReturnedForFixes', label: 'Returned for fixes' },
  { key: 'QARetest', label: 'QA retest' },
  { key: 'QAApproved', label: 'QA approved' },
  { key: 'ClientApproval', label: 'Client QA' },
  { key: 'Complete', label: 'Ready to Launch' },
  { key: 'Translations', label: 'Translations' },
  { key: 'SoftLaunch', label: 'Soft Launch' },
  { key: 'FullLaunch', label: 'Full Launch' },
  { key: 'DataProcessing', label: 'Data Processing' },
  { key: 'Completed', label: 'Completed' },
];

// Statuses with the client (Client Review, Client QA): shown in their own colour.
export const PROGRAMMING_CLIENT_STATUSES: ProgrammingStatusKey[] = ['ClientReview', 'ClientApproval'];

export type ProgrammingPhaseKey = 'review' | 'build' | 'qa' | 'field';

export interface ProgrammingPhase {
  key: ProgrammingPhaseKey;
  label: string;
  statuses: ProgrammingStatusKey[];
}

// The project page's phases. Review and Build happen on the SP Flow tab, QA on the QA Flow tab. The
// client hands off at the end of Review (Client Review → Approved) and inside QA (Client QA →
// Ready to Launch). The project page ends there; the Field phase is board-only.
export const PROGRAMMING_PHASES: ProgrammingPhase[] = [
  { key: 'review', label: 'Review', statuses: ['NotStarted', 'DocUploaded', 'Reviewing', 'ClientReview', 'Approved'] },
  { key: 'build', label: 'Build', statuses: ['Building'] },
  { key: 'qa', label: 'QA', statuses: ['ReadyForQA', 'QA', 'ReturnedForFixes', 'QARetest', 'QAApproved', 'ClientApproval', 'Complete'] },
];

// After Ready to Launch: the launch and post-field statuses, followed on the Programming board.
export const PROGRAMMING_FIELD_PHASE: ProgrammingPhase = {
  key: 'field', label: 'Field', statuses: ['Translations', 'SoftLaunch', 'FullLaunch', 'DataProcessing', 'Completed'],
};

// Every phase, for the Programming board and its reports.
export const PROGRAMMING_BOARD_PHASES: ProgrammingPhase[] = [...PROGRAMMING_PHASES, PROGRAMMING_FIELD_PHASE];

export function programmingPhaseFor(status: ProgrammingStatusKey | null | undefined): ProgrammingPhase {
  return PROGRAMMING_BOARD_PHASES.find(p => p.statuses.includes(status ?? 'NotStarted')) ?? PROGRAMMING_PHASES[0];
}

// Ready to Launch or later: programming is done.
export function isProgrammingDone(status: ProgrammingStatusKey | null | undefined): boolean {
  const order = PROGRAMMING_STATUSES.map(s => s.key);
  return order.indexOf(status ?? 'NotStarted') >= order.indexOf('Complete');
}

// A Decipher entry link is https://<host>/survey/<survey path>?...; its console page is
// https://<host>/apps/portal/#/projects/detail/<survey path>, on the link's own host so a survey on a
// client's Decipher account opens there. Same rule as the board's DecipherConsoleUrl on the API.
export function decipherConsoleUrl(surveyLink: string | null | undefined): string | null {
  const raw = surveyLink?.trim();
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  const marker = '/survey/';
  const at = url.pathname.toLowerCase().indexOf(marker);
  if (at < 0) return null;
  const path = url.pathname.substring(at + marker.length).replace(/^\/+|\/+$/g, '');
  return path ? `https://${url.hostname}/apps/portal/#/projects/detail/${path}` : null;
}

// The console page for a project's survey: its test link, else its live link, Decipher only.
export function projectDecipherConsoleUrl(segment: { surveyPlatForm?: string | null; testSurveyLink?: string | null; surveyLink?: string | null } | null | undefined): string | null {
  if (!segment || segment.surveyPlatForm !== 'Decipher') return null;
  return decipherConsoleUrl(segment.testSurveyLink) ?? decipherConsoleUrl(segment.surveyLink);
}

export function programmingStatusLabel(key: string | null | undefined): string {
  return PROGRAMMING_STATUSES.find(s => s.key === key)?.label ?? 'Not started';
}

// Events the frontend reports itself, because manual-testing records aren't keyed by project.
// SentForClientApproval is "Send to client" (internal); TestingFinalized is the client's "Sign off"
// (only in Client QA) or finalizing survey testing. Clients may report TestingCommentRaised and
// TestingFinalized; the server rejects the rest from them.
export type ProgrammingClientEvent = 'TestingCommentRaised' | 'SentForClientApproval' | 'TestingFinalized';

export interface ProgrammingStatusHistoryItem {
  id: string;
  fromStatus: ProgrammingStatusKey;
  toStatus: ProgrammingStatusKey;
  source: 'Manual' | 'Event';
  eventName: string | null;
  changedBy: string | null;
  changedByName: string | null;
  changedOn: string;
}

export interface ProgrammingStatusInfo {
  projectId: string;
  status: ProgrammingStatusKey;
  updatedOn: string | null;
  history: ProgrammingStatusHistoryItem[];
  team: ProgrammingBoardPerson[];
}

export interface ProgrammingBoardPerson {
  userId: string;
  name: string;
  role: 'Programmer' | 'Reviewer' | 'PM';
}

export interface ProgrammingBoardItem {
  projectId: string;
  projectCode: string;
  projectName: string;
  bidNumber: string | null;
  projectType: string | null;
  // The configured project type's Type and Name; used when projectType is blank or unrecognised.
  projectTypeConfigType: string | null;
  projectTypeConfigName: string | null;
  projectStatus: string;
  programmingStatus: ProgrammingStatusKey;
  programmingStatusUpdatedOn: string | null;
  programmingComplexity: string | null;
  clientId: string | null;
  clientName: string | null;
  surveyPlatform: string | null;
  testSurveyLink: string | null;
  // Internal users only; null when there's no Decipher survey link.
  decipherConsoleUrl: string | null;
  projectStartDate: string | null;
  projectEndDate: string | null;
  fieldStartDate: string | null;
  team: ProgrammingBoardPerson[];
  // Testing comments on the project's survey: open, and pending (awaiting sign-off).
  openComments: number;
  pendingComments: number;
}

// Tabs the combined Programming section can show; chosen per section in the project type.
export type ProgrammingSectionTab = 'sp-flow' | 'qa-flow';

// The Programming page's Trends view: projects on the board now or with a status change in the
// window (including ones that have since gone live), all their status changes, and the testing
// comments raised in the window.
export interface ProgrammingTrends {
  since: string;
  projects: ProgrammingBoardItem[];
  transitions: ProgrammingTrendTransition[];
  comments: ProgrammingTrendComment[];
}

export interface ProgrammingTrendTransition {
  projectId: string;
  fromStatus: ProgrammingStatusKey;
  toStatus: ProgrammingStatusKey;
  source: 'Manual' | 'Event';
  changedOn: string;
}

export interface ProgrammingTrendComment {
  projectId: string;
  commentType: string | null;
  status: string | null;
  createdOn: string;
}

export const PROGRAMMING_SECTION_TABS: { key: ProgrammingSectionTab; label: string }[] = [
  { key: 'sp-flow', label: 'SP Flow' },
  { key: 'qa-flow', label: 'QA Flow' },
];
